/* eslint-disable */
/**
 * useApiPolling — Polling hook med credentials, fejlhåndtering og cleanup
 *
 * @param {string}   endpoint      — Relativ sti fra /api (fx '/os/intern/statistik')
 * @param {number}   intervalMs    — Polling-interval i ms (default: 60000)
 * @param {object}   options
 *   .enabled    bool    — sæt false for at pausa polling (default: true)
 *   .onFejl     fn      — kaldes ved fejl med Error-objektet
 *   .transform  fn      — transformér response-data inden state-sæt
 *
 * @returns {{ data, loading, refreshing, fejl, opdateret, refresh }}
 *
 * loading vs. refreshing:
 *   `loading`    — true KUN ved første hentning (data === null endnu).
 *                  Brug denne til at vise skeleton/spinner første gang.
 *   `refreshing` — true under baggrunds-refresh (data er allerede tilgængeligt).
 *                  Brug denne til fx en diskret spinner i headeren — ALDRIG
 *                  til at skifte hele panelet ud, da det nulstiller scroll.
 *
 * 429-håndtering:
 *   Ved HTTP 429 (rate limit) afbryder vi IKKE — vi sætter blot en backoff-timer
 *   og forsøger igen. Fejl-state sættes IKKE ved 429, så UI viser aldrig
 *   "Kunne ikke vise denne sektion" pga. rate-limit.
 *   Backoff: 30s, 60s, 120s, 240s, 480s (cap). Nulstilles ved succeskald.
 *
 * Forbigående fejl-håndtering (deploy-blip):
 *   Ved HTTP 5xx eller netværksfejl (TypeError/fetch failed) forsøger hooken
 *   automatisk op til TRANSIENT_MAX_FORSØG gange med TRANSIENT_BACKOFF_MS
 *   mellemrum FØR den eksponerer fejl-state. Data bevares altid — eksisterende
 *   data nulstilles ALDRIG ved en midlertidig fejl. Fejl-state sættes kun
 *   efter alle retry-forsøg er udtømt, og kun ved vedvarende fejl.
 *   4xx fejl (ekskl. 429) er ægte fejl (auth, not-found) og retry'es IKKE.
 *
 * Kun-aktive-paneler:
 *   Sæt enabled=false for at stoppe al polling. Brugt af OSDashboardNy
 *   til at sætte enabled=false på paneler der ikke er det aktive panel.
 */

import { useState, useEffect, useCallback, useRef } from 'react';

const BACKOFF_TRIN         = [30000, 60000, 120000, 240000, 480000]; // ms — 429-backoff
const TRANSIENT_MAX_FORSØG = 3;    // maks retry ved 5xx/netværk
const TRANSIENT_BACKOFF_MS = 1500; // ms mellem transient-retry (1,5s)

// Afgør om en fejl er forbigående (server-genstart/deploy-blip/netværkshiccup)
// og bør retry'es stille — eller en ægte fejl der skal eksponeres.
function erForbigaaendeFejl(err, status) {
  if (status >= 500) return true;               // 5xx — server-fejl (502, 503, 504…)
  if (status === 0)  return true;               // CORS/netværk returnerede ingen status
  if (!status && err && (
    err.name === 'TypeError' ||                 // fetch() failed (netværk nede)
    err.message?.includes('Failed to fetch') ||
    err.message?.includes('Network request failed') ||
    err.message?.includes('Load failed')
  )) return true;
  return false;                                 // 4xx (401, 403, 404…) → ægte fejl
}

export default function useApiPolling(endpoint, intervalMs = 60000, options = {}) {
  const { enabled = true, onFejl, transform } = options;

  const [data,        setData]        = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [refreshing,  setRefreshing]  = useState(false);
  const [fejl,        setFejl]        = useState(null);
  const [opdateret,   setOpdateret]   = useState(null);

  // dataRef bruges til at skelne første load fra baggrunds-refresh
  // uden at skabe en closure-afhængighed på `data`-state.
  const dataRef           = useRef(null);
  const abortRef          = useRef(null);
  const timerRef          = useRef(null);
  const backoffRef        = useRef(0); // indeks i BACKOFF_TRIN (429-backoff)
  const transientRetryRef = useRef(0); // antal transient-retry-forsøg i indeværende kald

  const hent = useCallback(async () => {
    if (!enabled) return;

    // Afgør om dette er første load eller baggrunds-refresh
    const erFørsteLoad = dataRef.current === null;

    if (erFørsteLoad) {
      setLoading(true);
    } else {
      // Baggrunds-refresh: sæt kun refreshing — IKKE loading.
      // Det betyder at eksisterende data forbliver synlige og
      // komponenter forbliver monterede → scroll bevares.
      setRefreshing(true);
    }

    // Afbryd evt. igangværende fetch
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();

    // Nulstil transient-tæller ved hvert nyt kald (ikke ved intern retry)
    transientRetryRef.current = 0;

    // Indre retry-løkke: forsøger op til TRANSIENT_MAX_FORSØG gange ved
    // forbigående fejl (5xx/netværk) inden fejl-state eksponeres.
    // Bemærk: AbortController gælder per ydre kald — ved retry genbruges
    // den samme signal (er ikke afbrudt), så vi behøver ikke ny controller.
    let sidsteFejl = null;

    while (transientRetryRef.current <= TRANSIENT_MAX_FORSØG) {
      // Afbrudt udefra (tab-skift, unmount) — stop stille
      if (abortRef.current?.signal?.aborted) {
        setLoading(false);
        setRefreshing(false);
        return;
      }

      // Vent ved retry (ikke ved første forsøg)
      if (transientRetryRef.current > 0) {
        await new Promise(res => setTimeout(res, TRANSIENT_BACKOFF_MS));
        // Tjek abort igen efter sleep
        if (abortRef.current?.signal?.aborted) {
          setLoading(false);
          setRefreshing(false);
          return;
        }
      }

      try {
        const res = await fetch('/api' + endpoint, {
          credentials: 'include',
          signal:      abortRef.current.signal,
          headers:     { 'Cache-Control': 'no-cache' },
        });

        // ── 429: rate-limit — vent og forsøg igen, crasher ALDRIG UI ─────────
        if (res.status === 429) {
          const venteMs = BACKOFF_TRIN[Math.min(backoffRef.current, BACKOFF_TRIN.length - 1)];
          backoffRef.current = Math.min(backoffRef.current + 1, BACKOFF_TRIN.length - 1);
          clearInterval(timerRef.current);
          timerRef.current = setTimeout(() => {
            // Efter backoff: genoptag normal polling
            hent();
            if (intervalMs > 0) {
              timerRef.current = setInterval(hent, intervalMs);
            }
          }, venteMs);
          // Vis IKKE fejl til brugeren — blot stille backoff
          setLoading(false);
          setRefreshing(false);
          return;
        }

        // ── 5xx: forbigående server-fejl — retry stille ───────────────────────
        if (res.status >= 500) {
          sidsteFejl = new Error(`HTTP ${res.status}`);
          sidsteFejl._httpStatus = res.status;
          transientRetryRef.current += 1;
          continue; // prøv igen
        }

        if (!res.ok) {
          // 4xx (401, 403, 404…) — ægte fejl, retry hjælper ikke
          const body = await res.json().catch(() => ({}));
          const fejlObj = new Error(body.fejl || `HTTP ${res.status}`);
          fejlObj._httpStatus = res.status;
          // Data bevares (nulstilles IKKE) — UI viser måske stadig gammelt indhold
          setFejl(fejlObj);
          if (onFejl) onFejl(fejlObj);
          setLoading(false);
          setRefreshing(false);
          return;
        }

        // ── Succes ────────────────────────────────────────────────────────────
        backoffRef.current = 0;
        transientRetryRef.current = 0;

        const json = await res.json();
        const parsed = transform ? transform(json) : json;
        dataRef.current = parsed;
        setData(parsed);
        setFejl(null); // fjern evt. tidligere fejl-banner
        setOpdateret(new Date().toISOString());
        setLoading(false);
        setRefreshing(false);
        return;

      } catch (err) {
        if (err.name === 'AbortError') {
          // Afbrudt bevidst (unmount/tab-skift) — ignorer stille
          setLoading(false);
          setRefreshing(false);
          return;
        }

        // TypeError/netværksfejl — forbigående?
        if (erForbigaaendeFejl(err, 0)) {
          sidsteFejl = err;
          transientRetryRef.current += 1;
          continue; // prøv igen
        }

        // Ukendt fejl — eksponer straks
        sidsteFejl = err;
        break;
      }
    }

    // Alle forsøg udtømt (eller ægte fejl) — eksponér nu.
    // Data bevares stadig — UI kan vise gammelt indhold + discret fejl-indikator.
    if (sidsteFejl) {
      setFejl(sidsteFejl);
      if (onFejl) onFejl(sidsteFejl);
    }
    setLoading(false);
    setRefreshing(false);
  }, [endpoint, enabled, transform, onFejl, intervalMs]);

  // Første kald + polling — kun hvis enabled
  useEffect(() => {
    if (!enabled) {
      // Panel inaktivt — stop polling og ryd timer
      clearInterval(timerRef.current);
      clearTimeout(timerRef.current);
      if (abortRef.current) abortRef.current.abort();
      setLoading(false);
      setRefreshing(false);
      return;
    }

    // Nulstil dataRef ved endpoint-skift (fx tab-skift)
    dataRef.current = null;

    // 50 ms initial-delay eliminerer ERR_ABORTED i browser-konsollen:
    // React StrictMode (og hurtige visning-skift) monterer og afmonterer
    // effekter synkront — cleanup kører abort() inden fetch afsluttes,
    // hvilket giver røde ERR_ABORTED-linjer i DevTools (harmløst, men støj).
    // Med 50 ms delay er cleanup kørt FØR fetchen starter, og ingen
    // aborteret netværksanmodning vises.
    timerRef.current = setTimeout(() => {
      hent();
      if (intervalMs > 0) {
        timerRef.current = setInterval(hent, intervalMs);
      }
    }, 50);

    return () => {
      clearInterval(timerRef.current);
      clearTimeout(timerRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [hent, intervalMs, enabled]);

  return { data, loading, refreshing, fejl, opdateret, refresh: hent };
}
